import type { ChatResponse, ChatRuntime, ChatTurn } from '@its-simple-ai/callcenter-widget';
import type { UserContext, UserContextProvider } from './user-context';

export interface ContextAwareRuntimeOptions {
  apiUrl: string;
  product: 'ezdocu' | 'ezmig';
  tenantSlug: string;
  sessionId: string;
  language?: string;
  /**
   * Called right before every turn. Kept as a function (not a value) so
   * stale context never leaks — the widget is long-lived and `route`
   * changes as the user navigates.
   */
  getUserContext?: UserContextProvider;
  fetch?: typeof fetch;
}

/**
 * Drop-in replacement for `PublicWidgetRuntime` that decorates every POST
 * with the latest `UserContext` resolved at call time.
 *
 * We own the POST body format here because the vendored `@its-simple-ai/
 * callcenter-widget` 0.0.x does not yet surface `userContext` as a prop.
 * Once the SDK is republished with native support this class collapses to
 * a thin wrapper around the default runtime.
 */
export class ContextAwareRuntime implements ChatRuntime {
  private readonly apiUrl: string;
  private readonly product: 'ezdocu' | 'ezmig';
  private readonly tenantSlug: string;
  private readonly sessionId: string;
  private readonly language: string | undefined;
  private readonly getUserContext?: UserContextProvider;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ContextAwareRuntimeOptions) {
    this.apiUrl = opts.apiUrl.replace(/\/$/, '');
    this.product = opts.product;
    this.tenantSlug = opts.tenantSlug;
    this.sessionId = opts.sessionId;
    this.language = opts.language;
    this.getUserContext = opts.getUserContext;
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async sendTurn(history: ChatTurn[], signal?: AbortSignal): Promise<ChatResponse> {
    const userContext = this.getUserContext?.() ?? undefined;
    const sanitised = userContext ? sanitise(userContext) : undefined;

    const attCount = history.reduce((n, t) => n + (t.attachments?.length ?? 0), 0);
    if (typeof console !== 'undefined' && attCount > 0) {
      // eslint-disable-next-line no-console
      console.debug('[context-aware-runtime] sendTurn', {
        turns: history.length,
        attachments: attCount,
        kinds: history.flatMap((t) => t.attachments?.map((a) => a.kind) ?? []),
      });
    }

    const res = await this.fetchImpl(`${this.apiUrl}/public/widget/chat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        product: this.product,
        tenantSlug: this.tenantSlug,
        sessionId: this.sessionId,
        language: this.language,
        // Forward attachments when the widget attached images/PDFs to a turn.
        // Leaving the key off the payload keeps the DTO happy for the common
        // text-only case (the field is optional server-side).
        history: history.map((t) => ({
          role: t.role,
          content: t.content,
          ...(t.attachments && t.attachments.length > 0
            ? { attachments: t.attachments }
            : {}),
        })),
        userContext: sanitised,
      }),
      signal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`callcenter-widget chat HTTP ${res.status} ${body}`);
    }
    return (await res.json()) as ChatResponse;
  }
}

/**
 * Strips nullish fields so we send a small JSON and the backend DTO doesn't
 * reject the payload. Also drops empty strings/arrays/objects — those carry
 * no signal and only waste tokens in the system prompt.
 */
function sanitise(ctx: UserContext): UserContext | undefined {
  const out: UserContext = {};
  if (ctx.route) out.route = ctx.route;
  if (ctx.feature) out.feature = ctx.feature;
  if (ctx.userRole) out.userRole = ctx.userRole;
  if (ctx.accountPlan) out.accountPlan = ctx.accountPlan;
  if (ctx.routeParams && Object.keys(ctx.routeParams).length > 0) {
    out.routeParams = ctx.routeParams;
  }
  if (ctx.recentActions && ctx.recentActions.length > 0) {
    out.recentActions = ctx.recentActions;
  }
  if (ctx.featureFlags && Object.keys(ctx.featureFlags).length > 0) {
    out.featureFlags = ctx.featureFlags;
  }
  if (ctx.hints && Object.keys(ctx.hints).length > 0) {
    out.hints = ctx.hints;
  }
  if (ctx.visibleUi && ctx.visibleUi.length > 0) {
    out.visibleUi = ctx.visibleUi;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}
